import React from 'react';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { windowWrapper } from '@clubmed/utils';
import URI from 'urijs';
import { ID_MODAL_HEADER__BE__SAVE_CRITERIA_LOGIN } from 'Components/utils/identifiers/constants';
import { t } from 'Src/i18n';
import { useAppContext } from 'Context/ReactAppContext';
import * as pleUtils from 'Utils/ple-utils';
import { Modal } from 'Components/frameworks/modal/Modal';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import { COLORS } from 'Components/utils/color/colors';
import { Button } from 'Components/frameworks/button/Button';
import { Title } from 'Components/frameworks/title/Title';
import { useExtendedConnect } from 'Components/widgets/member-nav/member-sign-in/useExtendedConnect';
import {
  BE_SAVE_CRITERIA_QUERY,
  BE_SAVE_CRITERIA_NEW_ACCOUNT_QUERY,
} from 'Views/booking/constants';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
type Props = {
  closeModal: () => any;
  clickOrigin: 'header' | 'bottomBar' | null;
};
export function SaveCriteriaLogin({ closeModal, clickOrigin }: Props) {
  const appContext = useAppContext();
  const { isFacebookConnectAvailable } = useExtendedConnect();
  const { trackClickEvent } = useTrackEvent();

  const trackClick = (detailClick: string) => {
    trackClickEvent(
      clickOrigin === 'bottomBar'
        ? TRACK_EVENT_NAMES.CLICK_BOTTOM_BAR
        : TRACK_EVENT_NAMES.CLICK_HEADER,
      {
        detail_click: detailClick,
      },
    );
  };

  const currentUri = windowWrapper.getLocation();
  const redirectUri = new URI(currentUri).addSearch(BE_SAVE_CRITERIA_QUERY, 'true').toString();
  const redirectUriSignup = new URI(currentUri)
    .addSearch(BE_SAVE_CRITERIA_QUERY, 'true')
    .addSearch(BE_SAVE_CRITERIA_NEW_ACCOUNT_QUERY, 'true')
    .toString();
  return (
    <Modal
      ariaLabel={t('account.authentication.newToClubMed')}
      ariaLabelledby={ID_MODAL_HEADER__BE__SAVE_CRITERIA_LOGIN}
      closeLabel={t('common.close')}
      onClose={closeModal}
      size="small"
    >
      <p className="flex items-center gap-10 border-b-1 border-lightGrey px-10 py-15">
        <Icon className="shrink-0 text-sienna" name={ICONS.STAR_HOLLOW} width="1.625rem" />
        {t('booking.saveMyCriteria.createAccount')}
      </p>
      <div
        className="flex flex-col items-center bg-white px-10 py-40"
        data-testid="SaveCriteriaLogin-signin"
      >
        <Button
          className="w-full max-w-220"
          data-selector="saveCriteriaLogin-member-signIn"
          isExternalLink
          onClick={() => trackClick('travel_plans_login')}
          target="_self"
          url={pleUtils.buildSigninUrl({
            redirectUri,
            locale: appContext.locale,
          })}
        >
          {t('account.authentication.signin')}
        </Button>
        {isFacebookConnectAvailable && (
          <Button
            className="mt-10 w-full max-w-220 border-[#4267b2] bg-[#4267b2]"
            color={COLORS.WHITE}
            data-selector="saveCriteriaLogin-member-signIn-facebook"
            iconLeft={{
              name: ICONS.FACEBOOK,
              width: '1.5rem',
            }}
            isExternalLink
            target="_self"
            url={pleUtils.buildFacebookConnectUrl({
              redirectUri,
              locale: appContext.locale,
            })}
          >
            {t('account.authentication.connectionIncentive.facebookConnectButtonLabel')}
          </Button>
        )}
      </div>
      <div className="flex flex-col items-center gap-20 border-t-1 border-lightGrey bg-white px-10 py-25">
        <Title hLevel={2} id={ID_MODAL_HEADER__BE__SAVE_CRITERIA_LOGIN} theme="title-brand-n3">
          {t('account.authentication.newToClubMed')}
        </Title>
        <Button
          data-selector="saveCriteriaLogin-member-signUp"
          isExternalLink
          onClick={() => trackClick('travel_plans_sign_up')}
          target="_self"
          url={pleUtils.buildSignupUrl({
            redirectUri: redirectUriSignup,
            locale: appContext.locale,
          })}
        >
          {t('account.authentication.createAccountLinkLabel')}
        </Button>
      </div>
    </Modal>
  );
}
